import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { Booking } from "@/lib/services/bookings";
import { money, toAmount } from "@/lib/utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import { StyleSheet, View } from "react-native";

interface BookingPaymentCardProps {
  booking: Booking;
  showEarnings?: boolean;
  role: "driver" | "customer";
}

const formatPaymentDate = (dateStr?: string) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

const getPaymentStatusLabel = (booking: Booking) => {
  if (booking.payment_method === "cash") {
    return "Cash Payment";
  }
  switch (booking.payment?.status) {
    case "authorized":
      return "Payment Authorised";
    case "captured":
      return "Payment Captured";
    case "refunded":
      return "Payment Refunded";
    default:
      return "Payment";
  }
};

export const BookingPaymentCard: React.FC<BookingPaymentCardProps> = ({
  booking,
  showEarnings = false,
  role,
}) => {
  const { theme } = useAuth();
  if (!booking.payment) return null;
  const authDate =
    formatPaymentDate(booking.payment.authorized_at) ||
    formatPaymentDate(booking.payment.captured_at);
  const serviceCharge = toAmount(
    booking.pricing?.at_fee_net ?? booking.pricing?.service_charge,
  );
  const atVat = toAmount(booking.pricing?.at_vat_amount);
  const operatorVat = toAmount(booking.pricing?.operator_vat_amount);
  const operatorNet = booking.pricing?.operator_service_net;
  const netEarnings =
    operatorNet != null
      ? toAmount(booking.pricing?.operator_service_gross_amount)
      : toAmount(booking.pricing?.total_amount ?? booking.payment.amount) -
        serviceCharge -
        atVat;

  return (
    <View style={styles.section}>
      <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
        Payment
      </ThemedText>

      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.card,
            borderColor: theme.border,
            shadowColor: theme.background,
          },
        ]}
      >
        <ThemedText style={styles.amountLabel} type="extraSmall">
          AMOUNT
        </ThemedText>
        <ThemedText style={styles.amountValue} type="title">
          {role === "driver"
            ? money(netEarnings)
            : money(toAmount(booking.payment.amount))}
        </ThemedText>

        <View style={styles.methodBanner}>
          <Ionicons
            name={booking.payment_method === "cash" ? "cash" : "card-outline"}
            size={18}
            color="#EA580C"
          />
          <View style={styles.methodBannerText}>
            <ThemedText style={styles.methodTitle} type="smallSemiBold">
              {getPaymentStatusLabel(booking)}
            </ThemedText>
            {authDate ? (
              <ThemedText style={styles.methodSubtext} type="extraSmall">
                Authorised: {authDate}
              </ThemedText>
            ) : null}
          </View>
        </View>

        {booking.payment.tip_amount > 0 ? (
          <View style={styles.tipRow}>
            <ThemedText style={styles.breakdownLabel} type="small">
              Tip
            </ThemedText>
            <ThemedText style={styles.breakdownValue} type="smallSemiBold">
              {money(booking.payment.tip_amount)}
            </ThemedText>
          </View>
        ) : null}

        {showEarnings ? (
          <View style={styles.earningsBox}>
            {operatorNet != null ? (
              <View style={styles.breakdownRow}>
                <ThemedText style={styles.breakdownLabel} type="small">
                  Operator service (net)
                </ThemedText>
                <ThemedText style={styles.breakdownValue} type="smallSemiBold">
                  {money(toAmount(operatorNet))}
                </ThemedText>
              </View>
            ) : null}
            {operatorVat > 0 ? (
              <View style={styles.breakdownRow}>
                <ThemedText style={styles.breakdownLabel} type="small">
                  Operator VAT
                </ThemedText>
                <ThemedText style={styles.breakdownValue} type="smallSemiBold">
                  {money(operatorVat)}
                </ThemedText>
              </View>
            ) : null}
            <View style={styles.breakdownRow}>
              <ThemedText style={styles.breakdownLabel} type="small">
                AT platform fee (net)
              </ThemedText>
              <ThemedText style={styles.breakdownValue} type="smallSemiBold">
                -{money(serviceCharge)}
              </ThemedText>
            </View>
            {atVat > 0 ? (
              <View style={styles.breakdownRow}>
                <ThemedText style={styles.breakdownLabel} type="small">
                  AT VAT
                </ThemedText>
                <ThemedText style={styles.breakdownValue} type="smallSemiBold">
                  -{money(atVat)}
                </ThemedText>
              </View>
            ) : null}
            <View style={styles.breakdownRow}>
              <ThemedText style={styles.breakdownLabel} type="small">
                Your Earnings (Net)
              </ThemedText>
              <ThemedText style={styles.breakdownValue} type="smallSemiBold">
                {money(netEarnings)}
              </ThemedText>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 12,
  },
  card: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    gap: 12,
  },
  amountLabel: {
    color: BrandColors.mediumGray,
    fontSize: 11,
    fontWeight: "600",
    letterSpacing: 0.8,
  },
  amountValue: {
    fontSize: 28,
    fontWeight: "800",
    lineHeight: 34,
  },
  methodBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "#FFF7ED",
    borderRadius: 12,
    padding: 12,
  },
  methodBannerText: {
    flex: 1,
    gap: 2,
  },
  methodTitle: {
    color: "#EA580C",
    fontWeight: "700",
  },
  methodSubtext: {
    color: "#EA580C",
    opacity: 0.85,
  },
  tipRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  earningsBox: {
    backgroundColor: "#F3F4F6",
    borderRadius: 12,
    padding: 14,
    gap: 10,
  },
  breakdownRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breakdownLabel: {
    color: BrandColors.mediumGray,
  },
  breakdownValue: {
    color: BrandColors.secondary,
    fontWeight: "700",
  },
});
